// src/components/widgets/CreateWidgetModalShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  Code,
  Calendar,
  MessageCircle,
  Mail,
  AlertCircle,
  Check
} from 'lucide-react';

// shadcn components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';

import { useWidgets } from '../../hooks/useWidgets';
import { cn } from '../../utils/cn';

const CreateWidgetModalShadcn = ({ isOpen, onClose, onWidgetCreated }) => {
  const { widgets, createWidget, creatingWidget } = useWidgets();

  // Form state
  const [widgetName, setWidgetName] = useState('');
  const [widgetType, setWidgetType] = useState('booking');
  const [nameError, setNameError] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setWidgetName('');
      setWidgetType('booking');
      setNameError('');
      setHasInteracted(false);
    }
  }, [isOpen]);

  // Get default configuration for widget type
  const getDefaultConfiguration = (type) => {
    switch (type) {
      case 'booking':
        return {
          enabled_services: [],
          require_phone: true,
          require_notes: false,
          show_how_heard: true,
          show_sms_consent: true
        };
      case 'chat':
        return {
          welcome_message: 'Hello! How can we help you today?',
          auto_response_enabled: true
        };
      case 'contact':
        return {
          fields: ['name', 'email', 'message'],
          require_phone: false
        };
      default:
        return {};
    }
  };

  // Widget type options
  const widgetTypes = [
    {
      id: 'booking',
      name: 'Booking Widget',
      description: 'Allow customers to book appointments',
      icon: Calendar,
      available: true
    },
    {
      id: 'chat',
      name: 'Chat Widget',
      description: 'Live chat for customer support',
      icon: MessageCircle,
      available: false // Coming soon
    },
    {
      id: 'contact',
      name: 'Contact Widget',
      description: 'Simple contact form for inquiries',
      icon: Mail,
      available: false // Coming soon
    }
  ];

  // Real-time name validation
  const validateName = (name) => {
    // Check if empty
    if (!name.trim()) {
      return 'Widget name is required';
    }

    // Check minimum length
    if (name.trim().length < 2) {
      return 'Widget name must be at least 2 characters';
    }

    // Check maximum length
    if (name.length > 100) {
      return 'Widget name must be less than 100 characters';
    }

    // Check for duplicate names (case-insensitive, same type)
    const existingWidget = widgets.find(w =>
      w.widget_type === widgetType &&
      w.widget_name.toLowerCase().trim() === name.toLowerCase().trim()
    );

    if (existingWidget) {
      return `A ${widgetType} widget named "${name.trim()}" already exists`;
    }

    return null; // Valid
  };

  // Handle name input change
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setWidgetName(newName);
    setHasInteracted(true);

    // Validate in real-time
    const error = validateName(newName);
    setNameError(error);
  };

  // Handle type change
  const handleTypeChange = (newType) => {
    setWidgetType(newType);

    // Re-validate name with new type
    if (hasInteracted && widgetName) {
      const error = validateName(widgetName);
      setNameError(error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation
    const error = validateName(widgetName);
    if (error) {
      setNameError(error);
      setHasInteracted(true);
      return;
    }

    try {
      // Create the widget with proper object structure
      const widgetData = {
        widget_type: widgetType,
        widget_name: widgetName.trim(),
        is_active: false,
        primary_color: '#007bff',
        theme: 'auto',
        border_radius: 8,
        configuration: getDefaultConfiguration(widgetType)
      };

      createWidget(widgetData);

      // Notify parent component
      if (onWidgetCreated) {
        onWidgetCreated({
          type: widgetType,
          name: widgetName.trim()
        });
      }

      // Close modal
      onClose();
    } catch (error) {
      // Error handling is done in the store
      console.error('Failed to create widget:', error);
    }
  };

  // Check if form is valid
  const isFormValid = !nameError && widgetName.trim().length >= 2 && !creatingWidget;

  return (
    <Dialog open={isOpen} onOpenChange={!creatingWidget ? onClose : undefined}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Code className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Create New Widget</DialogTitle>
              <DialogDescription>Add a widget to your website</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Widget Name */}
          <div className="space-y-2">
            <Label htmlFor="widget-name">
              Widget Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="widget-name"
              type="text"
              value={widgetName}
              onChange={handleNameChange}
              placeholder="e.g., Main Booking Form, Contact Us"
              disabled={creatingWidget}
              className={cn(
                hasInteracted && nameError && "border-destructive focus-visible:ring-destructive"
              )}
              autoFocus
            />
            {hasInteracted && nameError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{nameError}</AlertDescription>
              </Alert>
            )}
            {hasInteracted && !nameError && widgetName.trim() && (
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <Check className="w-4 h-4" />
                <span>Name is available</span>
              </div>
            )}
          </div>

          {/* Widget Type */}
          <div className="space-y-3">
            <Label>
              Widget Type <span className="text-destructive">*</span>
            </Label>
            <RadioGroup value={widgetType} onValueChange={handleTypeChange} disabled={creatingWidget}>
              <div className="space-y-3">
                {widgetTypes.map((type) => {
                  const Icon = type.icon;

                  return (
                    <Card
                      key={type.id}
                      className={cn(
                        "cursor-pointer transition-all",
                        !type.available && "opacity-50 cursor-not-allowed",
                        widgetType === type.id && "ring-2 ring-primary"
                      )}
                      onClick={() => type.available && handleTypeChange(type.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <RadioGroupItem
                            value={type.id}
                            id={type.id}
                            disabled={!type.available || creatingWidget}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="w-5 h-5 text-muted-foreground" />
                              <Label htmlFor={type.id} className="font-medium cursor-pointer">
                                {type.name}
                              </Label>
                              {!type.available && (
                                <Badge variant="secondary">Coming Soon</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{type.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </RadioGroup>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={creatingWidget}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid}
              className="flex items-center gap-2"
            >
              {creatingWidget ? (
                <>
                  <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Code className="w-4 h-4" />
                  Create Widget
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWidgetModalShadcn;